<template>
  <Dialog
    :visible="isVisible"
    modal
    :draggable="false"
    :style="{ width: '720px' }"
    :dismissable-mask="!saving"
    @update:visible="emit('close')"
  >
    <template #header>
      <div :class="$style.dialogHeader">
        <h3>A/B Test targeting</h3>
        <span :class="$style.countChip">{{ offers.length }} {{ pluralize("offer", offers.length) }}</span>
      </div>
    </template>

    <div :class="$style.body">
      <div :class="$style.overwriteWarning">
        <VdIcon store="fas" name="triangle-exclamation" custom-size="16px" color="#b45309" />
        <span>
          This will
          <strong>overwrite</strong>
          any existing A/B Test targeting on all selected offers with the configuration below.
        </span>
      </div>

      <div :class="$style.applyingTo">
        <span :class="$style.applyingToLabel">Applying to</span>
        <span
          v-for="group in campaignGroups"
          :key="group.id"
          v-tooltip.top="group.offerNames.join(' · ')"
          :class="$style.campaignChip"
        >
          {{ group.name }}
          <span :class="$style.campaignChipCount">{{ group.count }}</span>
        </span>
      </div>

      <div :class="$style.applyingTo">
        <span :class="$style.applyingToLabel">{{ pluralize("Segment", segmentsInUse.length) }} in use</span>
        <span v-for="segment in segmentsInUse" :key="segment.id" :class="$style.campaignChip">
          {{ segment.name }}
          <span :class="$style.campaignChipCount">{{ segment.count }}</span>
        </span>
      </div>

      <div :class="$style.headerRow">
        <div>
          <h4 :class="$style.title">A/B Test targeting</h4>
          <p :class="$style.subtitle">
            Control whether these offers are
            <strong>included</strong>
            or
            <strong>excluded</strong>
            for specific A/B tests and their cohorts. An offer can either be excluded from selected cohorts, or
            restricted to only appear in selected cohorts — not both.
          </p>
        </div>
        <div :class="$style.toggle" role="group" aria-label="A/B test targeting mode">
          <button
            type="button"
            :class="[$style.toggleBtn, isExcluded ? $style.toggleActiveExclude : '']"
            :disabled="saving"
            @click="mode = 'EXCLUDE'"
          >
            <span :class="[$style.dot, isExcluded ? $style.dotExclude : $style.dotInactive]"></span>
            Exclude
          </button>
          <button
            type="button"
            :class="[$style.toggleBtn, !isExcluded ? $style.toggleActiveInclude : '']"
            :disabled="saving"
            @click="mode = 'INCLUDE'"
          >
            <span :class="[$style.dot, !isExcluded ? $style.dotInclude : $style.dotInactive]"></span>
            Include
          </button>
        </div>
      </div>

      <div :class="[$style.modeBanner, isExcluded ? $style.modeBannerExclude : $style.modeBannerInclude]">
        <VdIcon
          store="fas"
          :name="isExcluded ? 'ban' : 'circle-check'"
          custom-size="16px"
          :color="isExcluded ? '#b91c1c' : '#15803d'"
        />
        <span v-if="isExcluded">
          These offers will be
          <strong>excluded</strong>
          from the A/B tests &amp; cohorts listed below. They show everywhere else.
        </span>
        <span v-else>
          These offers will
          <strong>only</strong>
          appear for the A/B tests &amp; cohorts listed below. They are hidden everywhere else.
        </span>
      </div>

      <div :class="$style.sectionLabel">{{ isExcluded ? "Excluded" : "Included" }} A/B tests &amp; cohorts</div>

      <div v-if="!linkableTests.length" :class="$style.emptyEntries">
        No dynamic A/B test is available in this version to target these offers with.
      </div>
      <div v-else-if="!entries.length" :class="$style.emptyEntries">
        No A/B tests configured yet. These offers are unaffected by any A/B test targeting.
      </div>

      <div v-for="(entry, index) in entries" :key="index" :class="$style.testEntry">
        <div :class="$style.testEntryHeader">
          <div :class="$style.testSelectWrapper">
            <label :class="$style.testSelectLabel">A/B test</label>
            <Select
              v-model="entry.abTestId"
              :options="availableTestsFor(entry)"
              option-value="id"
              option-label="name"
              placeholder="Select an A/B test..."
              :class="$style.testSelect"
              @update:model-value="entry.cohorts = {}"
            >
              <template #option="slotProps">
                <span>{{ slotProps.option.name }} · {{ formatState(slotProps.option.state) }}</span>
              </template>
            </Select>
          </div>
          <Button icon="pi pi-times" text severity="secondary" :disabled="saving" @click="removeEntry(index)" />
        </div>

        <div v-if="entry.abTestId" :class="$style.cohortsSection">
          <div :class="$style.cohortsHeader">
            <span>Cohorts to {{ isExcluded ? "exclude" : "include" }}</span>
          </div>
          <div :class="$style.cohortRows">
            <div
              v-for="cohort in cohortsOf(entry.abTestId)"
              :key="cohort.id"
              :class="[$style.cohortRow, entry.cohorts[cohort.id] ? $style.cohortRowActive : '']"
            >
              <div :class="$style.cohortRowHeader">
                <label :class="$style.cohortOption">
                  <Checkbox
                    :model-value="!!entry.cohorts[cohort.id]"
                    :binary="true"
                    :disabled="saving"
                    @update:model-value="(checked: boolean) => toggleCohort(entry, cohort.id, checked)"
                  />
                  {{ cohort.name }}
                  <span :class="$style.cohortAllocation">{{ cohort.allocationRate }}%</span>
                </label>
                <div
                  v-if="entry.cohorts[cohort.id]"
                  :class="$style.linkModeToggle"
                  role="group"
                  aria-label="Cohort linking mode"
                >
                  <button
                    type="button"
                    :class="[
                      $style.linkModeBtn,
                      entry.cohorts[cohort.id].mode === 'auto' ? $style.linkModeActiveAuto : '',
                    ]"
                    :disabled="saving"
                    @click="setLinkMode(entry, cohort.id, 'auto')"
                  >
                    Auto · by segment
                  </button>
                  <button
                    type="button"
                    :class="[
                      $style.linkModeBtn,
                      entry.cohorts[cohort.id].mode === 'pick' ? $style.linkModeActivePick : '',
                    ]"
                    :disabled="saving"
                    @click="setLinkMode(entry, cohort.id, 'pick')"
                  >
                    Pick treatments{{
                      entry.cohorts[cohort.id].mode === "pick" && entry.cohorts[cohort.id].segmentIds.length
                        ? ` · ${entry.cohorts[cohort.id].segmentIds.length}`
                        : ""
                    }}
                  </button>
                </div>
              </div>

              <p v-if="entry.cohorts[cohort.id]?.mode === 'auto'" :class="$style.cohortHint">
                Each offer links to the treatment matching its own segment.
              </p>

              <div v-else-if="entry.cohorts[cohort.id]?.mode === 'pick'" :class="$style.treatmentsPanel">
                <div :class="$style.treatmentsHeader">
                  <span>
                    All {{ offers.length }} {{ pluralize("offer", offers.length) }} link to each checked treatment
                  </span>
                  <a :class="$style.selectAllLink" @click="toggleAllTreatments(entry, cohort.id)">
                    {{
                      entry.cohorts[cohort.id].segmentIds.length === dynamicSegmentsOf(entry.abTestId).length
                        ? "Clear all"
                        : "Select all"
                    }}
                  </a>
                </div>
                <div :class="$style.treatmentsGrid">
                  <label
                    v-for="segmentId in dynamicSegmentsOf(entry.abTestId)"
                    :key="segmentId"
                    :class="$style.treatmentOption"
                  >
                    <Checkbox
                      :model-value="entry.cohorts[cohort.id].segmentIds.includes(segmentId)"
                      :binary="true"
                      :disabled="saving"
                      @update:model-value="(checked: boolean) => toggleTreatment(entry, cohort.id, segmentId, checked)"
                    />
                    <span>{{ segmentName(segmentId) }}</span>
                  </label>
                </div>
                <p v-if="!entry.cohorts[cohort.id].segmentIds.length" :class="$style.treatmentError">
                  Check at least one treatment, or switch back to Auto.
                </p>
              </div>
            </div>
          </div>
          <p v-if="!selectedCohortCount(entry)" :class="$style.cohortHint">
            Select at least one cohort of {{ testName(entry.abTestId) }} to {{ isExcluded ? "exclude" : "include" }}.
          </p>

          <div v-if="autoSkipInfo(entry)" :class="$style.skipWarning">
            <VdIcon store="fas" name="triangle-exclamation" custom-size="14px" color="#b45309" />
            <span>
              {{ autoSkipInfo(entry)!.offerCount }} {{ pluralize("offer", autoSkipInfo(entry)!.offerCount) }} in
              {{ autoSkipInfo(entry)!.segmentCount }}
              {{ pluralize("segment", autoSkipInfo(entry)!.segmentCount) }} ({{ autoSkipInfo(entry)!.segmentNames }})
              are not covered by this test's dynamic segments — auto linking skips them in
              {{ autoSkipInfo(entry)!.cohortNames }}. Use Pick treatments to place them.
            </span>
          </div>
        </div>
      </div>

      <button type="button" :class="$style.addTestBtn" :disabled="!canAddEntry || saving" @click="addEntry">
        + Add A/B test
      </button>

      <div :class="$style.summary">
        <div :class="$style.sectionLabel">Configuration summary</div>
        <p v-if="!summaryGroups.length && isExcluded">
          Not excluded from any A/B test. In
          <strong :class="$style.excludeText">Exclude</strong>
          mode with no tests, these offers are served to everyone in their segments.
        </p>
        <p v-else-if="!summaryGroups.length" :class="$style.includeEmptyWarning">
          Not linked to any A/B test. In
          <strong>Include</strong>
          mode with no links, these offers are served to no one.
        </p>
        <template v-else>
          <p>
            These offers will be
            <strong :class="isExcluded ? $style.excludeText : $style.includeText">
              {{ isExcluded ? "excluded from" : "shown only in" }}
            </strong>
            the following A/B test cohort treatments:
          </p>
          <div v-for="group in summaryGroups" :key="group.abTestId" :class="$style.summaryGroup">
            <div :class="$style.summaryTestName">{{ group.testName }}</div>
            <div v-for="row in group.cohorts" :key="row.cohortId" :class="$style.summaryCohortRow">
              <span :class="$style.summaryCohortName">
                {{ row.cohortName }}
                <span :class="$style.cohortAllocation">{{ row.allocationRate }}%</span>
              </span>
              <span :class="$style.summaryMode">
                {{ row.mode === "pick" ? `all ${offers.length} offers →` : "auto →" }}
              </span>
              <span v-for="chip in row.chips" :key="chip.segmentId" :class="$style.treatmentChip">
                {{ segmentName(chip.segmentId) }}
                <span v-if="chip.count !== undefined" :class="$style.treatmentChipCount">{{ chip.count }}</span>
              </span>
              <span v-if="row.skippedCount" :class="$style.skippedText">
                {{ row.skippedCount }} {{ pluralize("offer", row.skippedCount) }} skipped
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <div :class="$style.footer">
        <Button label="Cancel" severity="secondary" outlined :disabled="saving" @click="emit('close')" />
        <Button label="Apply to selected offers" :loading="saving" :disabled="!canApply" @click="apply" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import Select from "primevue/select";
import { computed, ref, watch } from "vue";

import useBackendClient from "@/client";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { pluralize } from "@/filters";
import { useBulkAbtestTargetingOptionsQuery } from "@/graphql";
import useToast from "@/hooks/useToastNotification";

import { useSegments } from "../useSegments";

type OfferLike = {
  id: string;
  name: string;
  campaignId?: string | null;
  conditions?: { segments?: string[] | null } | null;
};

// Per selected cohort: "auto" links each offer via its own segment's treatment; "pick" links ALL offers to
// each treatment checked below (treatments are addressed by their dynamic segment id — a treatment is
// unique per (cohort, segment) and created on demand server-side, so it need not exist yet).
type CohortConfig = { mode: "auto" | "pick"; segmentIds: string[] };
type Entry = { abTestId: string | null; cohorts: Record<string, CohortConfig> };

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  // The selected offer objects (id, name, campaignId, conditions.segments).
  offers: {
    type: Array,
    default: () => [],
  },
  // Campaign list ({ id, name }) used to resolve chip names.
  campaigns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["close", "applied"]);

const { toastError } = useToast();
const { client } = useBackendClient();

const mode = ref("EXCLUDE");
const entries = ref<Entry[]>([]);
const saving = ref(false);

const isExcluded = computed(() => mode.value === "EXCLUDE");

const typedOffers = computed(() => props.offers as OfferLike[]);

// Reset the form each time the modal opens.
watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      mode.value = "EXCLUDE";
      entries.value = [];
      saving.value = false;
    }
  }
);

const { result: optionsResult } = useBulkAbtestTargetingOptionsQuery(
  { gameId: props.gameId, versionId: props.versionId },
  { fetchPolicy: "cache-and-network" }
);

const { segments: allSegments } = useSegments(props.gameId);
const segmentNameById = computed(() => new Map((allSegments.value ?? []).map((segment) => [segment.id, segment.name])));
const segmentName = (segmentId: string) => segmentNameById.value.get(segmentId) ?? segmentId;

// All segments used by the selected offers (offers may use different segments), with the count of offers
// carrying each — shown as chips.
const segmentsInUse = computed(() => {
  const counts = new Map<string, number>();
  typedOffers.value.forEach((offer) => {
    (offer.conditions?.segments ?? []).forEach((segmentId) => {
      counts.set(segmentId, (counts.get(segmentId) ?? 0) + 1);
    });
  });
  return [...counts.entries()].map(([id, count]) => ({ id, name: segmentName(id), count }));
});

// Auto linking places each offer via its FIRST segment (server parity), so warnings and the summary count
// offers by their first segment, not by every segment they carry.
const firstSegmentCounts = computed(() => {
  const counts = new Map<string, number>();
  typedOffers.value.forEach((offer) => {
    const first = offer.conditions?.segments?.[0];
    if (first) {
      counts.set(first, (counts.get(first) ?? 0) + 1);
    }
  });
  return counts;
});
const offersWithoutSegmentCount = computed(
  () => typedOffers.value.filter((offer) => !offer.conditions?.segments?.length).length
);

// Any dynamic, non-archived test is linkable — offers' segments do NOT have to match the test's dynamic
// segments: auto linking skips uncovered offers (warned per test), and Pick treatments places every offer
// explicitly.
const linkableTests = computed(() =>
  (optionsResult.value?.game?.version?.abtests ?? []).filter(
    (abtest) => abtest.isDynamic && !["ARCHIVED", "STOPPED"].includes(abtest.state as string)
  )
);

const testsById = computed(() => new Map(linkableTests.value.map((abtest) => [abtest.id, abtest])));

const availableTestsFor = (entry: Entry) => {
  const pickedElsewhere = new Set(entries.value.filter((e) => e !== entry && e.abTestId).map((e) => e.abTestId));
  return linkableTests.value.filter((abtest) => !pickedElsewhere.has(abtest.id));
};

const cohortsOf = (abTestId: string | null) => testsById.value.get(abTestId ?? "")?.cohorts ?? [];
const testName = (abTestId: string | null) => testsById.value.get(abTestId ?? "")?.name ?? "";
const dynamicSegmentsOf = (abTestId: string | null) => testsById.value.get(abTestId ?? "")?.dynamicSegmentIds ?? [];
const formatState = (state: string) => state.charAt(0) + state.slice(1).toLowerCase();

const toggleCohort = (entry: Entry, cohortId: string, checked: boolean) => {
  if (checked) {
    entry.cohorts[cohortId] = { mode: "auto", segmentIds: [] };
  } else {
    delete entry.cohorts[cohortId];
  }
};

// Switching modes is non-destructive: the treatment picks are kept until the cohort is unchecked.
const setLinkMode = (entry: Entry, cohortId: string, linkMode: "auto" | "pick") => {
  const config = entry.cohorts[cohortId];
  if (config) {
    config.mode = linkMode;
  }
};

const toggleTreatment = (entry: Entry, cohortId: string, segmentId: string, checked: boolean) => {
  const config = entry.cohorts[cohortId];
  if (!config) {
    return;
  }
  config.segmentIds = checked
    ? [...new Set([...config.segmentIds, segmentId])]
    : config.segmentIds.filter((id) => id !== segmentId);
};

const toggleAllTreatments = (entry: Entry, cohortId: string) => {
  const config = entry.cohorts[cohortId];
  if (!config) {
    return;
  }
  const all = dynamicSegmentsOf(entry.abTestId);
  config.segmentIds = config.segmentIds.length === all.length ? [] : [...all];
};

const addEntry = () => {
  entries.value.push({ abTestId: null, cohorts: {} });
};

const removeEntry = (index: number) => {
  entries.value.splice(index, 1);
};

const selectedCohortCount = (entry: Entry) => Object.keys(entry.cohorts).length;

const canAddEntry = computed(() => entries.value.length < linkableTests.value.length);

// Aggregated per-test warning: offers whose FIRST segment is not a dynamic segment of the test have no auto
// placement — they are skipped in every auto cohort of this entry (never per-offer text: must stay readable
// with hundreds of offers).
const autoSkipInfo = (entry: Entry) => {
  const test = testsById.value.get(entry.abTestId ?? "");
  if (!test) {
    return null;
  }
  const autoCohorts = cohortsOf(entry.abTestId).filter((cohort) => entry.cohorts[cohort.id]?.mode === "auto");
  if (!autoCohorts.length) {
    return null;
  }

  const dynamicSegments = new Set(test.dynamicSegmentIds ?? []);
  const uncovered = [...firstSegmentCounts.value].filter(([segmentId]) => !dynamicSegments.has(segmentId));
  const offerCount = uncovered.reduce((sum, [, count]) => sum + count, 0) + offersWithoutSegmentCount.value;
  if (!offerCount) {
    return null;
  }

  const names = uncovered.map(([segmentId]) => segmentName(segmentId));
  if (offersWithoutSegmentCount.value) {
    names.push("no segment");
  }
  const shownNames = names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} more` : "");

  return {
    offerCount,
    segmentCount: names.length,
    segmentNames: shownNames,
    cohortNames: autoCohorts.map((cohort) => cohort.name).join(", "),
  };
};

// Configuration summary, grouped by test → cohort and AGGREGATED (chips with offer counts, one skipped
// counter) so it stays readable when hundreds of offers are selected.
const summaryGroups = computed(() =>
  entries.value
    .filter((entry) => entry.abTestId && selectedCohortCount(entry) > 0)
    .map((entry) => {
      const test = testsById.value.get(entry.abTestId ?? "");
      const dynamicSegments = new Set(test?.dynamicSegmentIds ?? []);
      const cohorts = cohortsOf(entry.abTestId)
        .filter((cohort) => entry.cohorts[cohort.id])
        .map((cohort) => {
          const config = entry.cohorts[cohort.id];
          if (config.mode === "pick") {
            return {
              cohortId: cohort.id,
              cohortName: cohort.name,
              allocationRate: cohort.allocationRate,
              mode: "pick" as const,
              chips: config.segmentIds.map((segmentId) => ({
                segmentId,
                count: undefined as number | undefined,
              })),
              skippedCount: 0,
            };
          }
          let placed = 0;
          const chips = [...firstSegmentCounts.value]
            .filter(([segmentId]) => dynamicSegments.has(segmentId))
            .map(([segmentId, count]) => {
              placed += count;
              return { segmentId, count };
            });
          return {
            cohortId: cohort.id,
            cohortName: cohort.name,
            allocationRate: cohort.allocationRate,
            mode: "auto" as const,
            chips,
            skippedCount: typedOffers.value.length - placed,
          };
        });
      return { abTestId: entry.abTestId as string, testName: test?.name ?? "", cohorts };
    })
);

// Every entry needs a test and at least one cohort; every Pick-treatments cohort needs at least one
// treatment checked (an explicit incomplete state — auto needs nothing more).
const canApply = computed(
  () =>
    !saving.value &&
    entries.value.every(
      (entry) =>
        entry.abTestId &&
        selectedCohortCount(entry) > 0 &&
        Object.values(entry.cohorts).every((config) => config.mode === "auto" || config.segmentIds.length > 0)
    )
);

const campaignGroups = computed(() => {
  const campaignNamesById = new Map(
    (props.campaigns as { id: string; name: string }[]).map((campaign) => [campaign.id, campaign.name])
  );
  const groups = new Map<string, { id: string; name: string; count: number; offerNames: string[] }>();
  typedOffers.value.forEach((offer) => {
    const id = offer.campaignId || "noCampaign";
    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name: campaignNamesById.get(id) || "No campaign",
        count: 0,
        offerNames: [],
      });
    }
    const group = groups.get(id)!;
    group.count += 1;
    group.offerNames.push(offer.name);
  });
  return [...groups.values()];
});

const apply = async () => {
  saving.value = true;
  try {
    const { data } = await client.put(`/app/${props.gameId}/version/${props.versionId}/offer/abtest-links`, {
      offerIds: typedOffers.value.map((offer) => offer.id),
      abtestLinkMode: mode.value,
      selections: entries.value.map((entry) => ({
        abTestId: entry.abTestId,
        cohorts: Object.entries(entry.cohorts).map(([cohortId, config]) => ({
          cohortId,
          ...(config.mode === "pick" ? { treatmentSegmentIds: config.segmentIds } : {}),
        })),
      })),
    });
    emit("applied", data);
  } catch (error) {
    toastError({
      detail:
        (error as { response?: { data?: { details?: string } } })?.response?.data?.details ||
        "An error occurred while updating the offers' A/B-test targeting",
      summary: "Error",
      life: 5 * 1000,
    });
  } finally {
    saving.value = false;
  }
};
</script>

<style module lang="scss">
.dialogHeader {
  display: flex;
  align-items: center;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 18px;
  }
}

.countChip {
  padding: 3px 10px;
  border-radius: 10px;
  background-color: #e0e7ff;
  color: #3730a3;
  font-size: 13px;
  font-weight: 600;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.overwriteWarning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 14px;
  line-height: 1.45;
}

.applyingTo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 8px;
}

.applyingToLabel {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  margin-right: 4px;
}

.campaignChip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  cursor: default;
}

.campaignChipCount {
  padding: 1px 8px;
  border-radius: 999px;
  background-color: var(--vd-color-blue-default, #3b82f6);
  color: #fff;
  font-size: 12px;
}

.headerRow {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.subtitle {
  margin: 0;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  max-width: 420px;
}

.toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background-color: var(--vd-color-neutral-light-10, #f3f4f6);
  flex-shrink: 0;
}

.toggleBtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.toggleBtn:disabled {
  cursor: default;
}

.toggleActiveInclude {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #15803d;
}

.toggleActiveExclude {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #b91c1c;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dotInclude {
  background: #16a34a;
}

.dotExclude {
  background: #dc2626;
}

.dotInactive {
  background: #9ca3af;
}

.modeBanner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.modeBannerInclude {
  background-color: #dcfce7;
  color: #15803d;
}

.modeBannerExclude {
  background-color: #fee2e2;
  color: #b91c1c;
}

.sectionLabel {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.emptyEntries {
  padding: 20px 16px;
  border: 1px dashed var(--vd-color-neutral-light-6, #d1d5db);
  border-radius: 8px;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

.testEntry {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 12px;
}

.testEntryHeader {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.testSelectWrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.testSelectLabel {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.testSelect {
  width: 100%;
}

.cohortsSection {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px dashed var(--vd-color-neutral-light-8, #e5e7eb);
  padding-top: 12px;
}

.cohortsHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
}

.selectAllLink {
  color: var(--vd-color-blue-default, #3b82f6);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.cohortRows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cohortRow {
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 8px;
  padding: 4px 12px;
}

.cohortRowActive {
  border-color: var(--vd-color-neutral-light-6, #d1d5db);
}

.cohortRowHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 6px 0;
}

.cohortOption {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.cohortAllocation {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-weight: 400;
}

.linkModeToggle {
  display: inline-flex;
  gap: 3px;
  padding: 3px;
  border-radius: 8px;
  background-color: var(--vd-color-neutral-light-10, #f3f4f6);
}

.linkModeBtn {
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  background: transparent;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.linkModeBtn:disabled {
  cursor: default;
}

.linkModeActiveAuto {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #15803d;
}

.linkModeActivePick {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #534ab7;
}

.cohortHint {
  margin: 0 0 8px;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-style: italic;
}

.treatmentsPanel {
  border-top: 1px dashed var(--vd-color-neutral-light-8, #e5e7eb);
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.treatmentsHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
  font-weight: 600;
}

.treatmentsGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 4px 12px;
}

.treatmentOption {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}

.treatmentError {
  margin: 0;
  color: #b91c1c;
  font-size: 13px;
}

.skipWarning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 13px;
  line-height: 1.45;
}

.addTestBtn {
  align-self: flex-start;
  padding: 10px 18px;
  border: 1px dashed var(--vd-color-blue-default, #3b82f6);
  border-radius: 8px;
  background: var(--vd-color-neutral-light-10, #f8fafc);
  color: var(--vd-color-blue-default, #3b82f6);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 12px;
  background-color: var(--vd-color-neutral-light-10, #f9fafb);
  font-size: 14px;

  p {
    margin: 0;
    line-height: 1.5;
  }
}

.summaryGroup {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summaryTestName {
  font-size: 13px;
  font-weight: 600;
}

.summaryCohortRow {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: 14px;
}

.summaryCohortName {
  font-size: 13px;
}

.summaryMode {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
}

.treatmentChip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: #e0e7ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.treatmentChipCount {
  opacity: 0.75;
  font-weight: 400;
}

.skippedText {
  color: #b91c1c;
  font-size: 12px;
  white-space: nowrap;
}

.includeEmptyWarning {
  color: #b91c1c;
  font-weight: 600;
}

.excludeText {
  color: #b91c1c;
}

.includeText {
  color: #15803d;
}
</style>
